import EmailIcon from "@mui/icons-material/Email";
import { Box, Button, Grid } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { isFulfilled, isRejected } from "@reduxjs/toolkit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { Error } from "../../common/components/functional/error/Error";
import {
  FilterSearchField,
  SearchFilter,
} from "../../common/components/functional/FilterSearchField/FilterSearchField";
import LoadingSpinner from "../../common/components/functional/loadingSpinner/LoadingSpinner";
import { TALENTS_PAGE_SIZE } from "../../common/components/functional/pagination/constants";
import { TablePaginationProps } from "../../common/components/functional/pagination/types";
import { SelectionActionBar } from "../../common/components/functional/selectionActionBar/SelectionActionBar";
import { StripedTable } from "../../common/components/functional/stripedTable/StripedTable";
import { Heading1 } from "../../common/components/styled/styled";
import { stringToList } from "../../common/helpers/common";
import filterFalsyParameters from "../../common/helpers/filterFalsyParameters";
import { isSearchTermsFilter } from "../../common/helpers/filters";
import isEmpty from "../../common/helpers/isEmpty";

import { Nullable } from "../../common/types/nullable";
import { Hits } from "../../common/types/Paginated";
import { useTablePagination } from "../../hooks/useTablePagination";
import { talentsDashboardCounts } from "../dashboard/slice/dashboardActions";
import {
  getFiltersFromLocalStorage,
  getQuickFilterFromLocalStorage,
  removeQuickFilterFromLocalStorage,
  setFiltersToLocalStorage,
  setQuickFilterToLocalStorage,
} from "../dashboard/talents/helpers";
import { TalentQuickFilterButton } from "../dashboard/talents/types";
import { fetchTalentTags } from "../talentTags/talentTagsSlice";
import {
  TALENT_DEFAULT_FILTERS,
  TALENT_FILTERS_LOCAL_STORAGE,
} from "./constants";
import TalentFilters from "./filters/TalentFilters";
import TalentQuickFilterButtons2 from "./filters/TalentQuickFilterButtons2";
import { updateTalentsList } from "./helpers";
import SendTalentsUserMessageDialog from "./sendUserMessage/SendTalentsUserMessageDialog";
import TalentSearchInfo from "./TalentSearchInfo";
import { Talent, TalentsSortBy } from "./talentAPI";
import { fetchTalentsPage } from "./talentsSlice";
import { TalentTableRow } from "./TalentTableRow";
import { TalentStatus } from "./types/common";

export const TalentsPage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("talentProfile");

  const [allTalents, setAllTalents] = useState<Talent[]>([]);
  const [hits, setHits] = useState<Hits>();
  const [cursor, setCursor] = useState<Nullable<string>>(null);
  const [error, setError] = useState<boolean>(false);
  const [nameSearch, setNameSearch] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
  const [searchInitiated, setSearchInitiated] = useState(false);
  const [selectedTalentIds, setSelectedTalentIds] = useState<string[]>([]);
  const [sendUserMessageDialogOpen, setSendUserMessageDialogOpen] =
    useState<boolean>(false);

  const [selectedQuickFilter, setSelectedQuickFilter] = useState<
    TalentQuickFilterButton | ""
  >(getQuickFilterFromLocalStorage());

  const [filters, setFilters] = useState<SearchFilter[]>(
    getFiltersFromLocalStorage() || TALENT_DEFAULT_FILTERS,
  );

  const [sortBy, setSortBy] = useState<TalentsSortBy>("name_asc");

  const { talentCounts } = useAppSelector((state) => state.dashboard);

  const hasSearchTerms = useMemo(
    () => filters.some((f) => isSearchTermsFilter(f) && !isEmpty(f.value)),
    [filters],
  );

  const customerOrgFilter = useMemo(
    () => filters.find((f) => f.name === "customerOrgId" && !isEmpty(f.value)),
    [filters],
  );

  const customerOrgId = customerOrgFilter?.value as string | undefined;
  const hasCustomerOrgFilter = !!customerOrgId;

  const sortOptions = useMemo(
    () => [
      { value: "name_asc", label: t("sortAlphaAsc") },
      { value: "name_desc", label: t("sortAlphaDesc") },
      { value: "memberSince_desc", label: t("sortNewestFirst") },
      { value: "memberSince_asc", label: t("sortOldestFirst") },
      { value: "score", label: t("sortScore"), disabled: !hasSearchTerms },
    ],
    [t, hasSearchTerms],
  );

  useEffect(() => {
    if (hasSearchTerms) {
      setSortBy("score");
    } else if (sortBy === "score") {
      setSortBy("name_asc");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSearchTerms]);

  useEffect(() => {
    // Fetch talents tags to have names for ids
    dispatch(fetchTalentTags({}));
    if (!talentCounts.counts) {
      dispatch(talentsDashboardCounts({}));
    }
  }, [dispatch, talentCounts.counts]);

  useEffect(() => {
    setSelectedTalentIds([]);
    setSendUserMessageDialogOpen(false);
  }, [customerOrgId]);

  const dispatchSearch = useCallback(
    async (cursor?: Nullable<string>) => {
      setSearchInitiated(true);

      const filterObject = filters.reduce(
        (acc, filter) => {
          const { name, value } = filter;

          if (isSearchTermsFilter(filter) && value) {
            const terms = Array.isArray(value)
              ? value
              : stringToList(value as string, ",");
            if (!isEmpty(terms)) {
              acc.searchTerms = terms.join(",");
            }
          } else if (!isEmpty(value)) {
            acc[name] = value;
          }

          return acc;
        },
        {} as Record<string, any>,
      );

      if (nameSearch.trim()) {
        filterObject.search = nameSearch.trim();
      }

      setIsLoading(true);

      const searchResults = await dispatch(
        fetchTalentsPage({
          ...(cursor !== undefined && cursor !== null && { cursor }),
          pageSize: TALENTS_PAGE_SIZE,
          sortBy,
          ...filterFalsyParameters(filterObject),
        }),
      );

      if (isFulfilled(searchResults)) {
        setAllTalents((prevTalents) => {
          const result = searchResults.payload.data as Talent[];
          return updateTalentsList(prevTalents, result, cursor);
        });
        setHits(searchResults.payload.hits);
        setCursor(searchResults.payload.cursor);
      }

      if (isRejected(searchResults)) {
        setError(true);
      }

      setIsLoading(false);
      setSearchInitiated(false);
    },
    [dispatch, filters, nameSearch, sortBy],
  );

  const { pageData, count, onPageChange, pageIndex, resetPagination } =
    useTablePagination<Talent>({
      data: allTalents,
      cursor,
      hits,
      pageSize: parseInt(TALENTS_PAGE_SIZE, 10),
      fetchNext: (cursor) => dispatchSearch(cursor),
      preventDispatch: true,
    });

  const handleSortChange = useCallback(
    (event: SelectChangeEvent<string | string[]>) => {
      setSortBy(event.target.value as TalentsSortBy);
      resetPagination();
    },
    [resetPagination],
  );

  useEffect(() => {
    void dispatchSearch();
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filters, dispatchSearch]);

  // Reset searchInitiated when allTalents change
  useEffect(() => {
    setSearchInitiated(false);
  }, [allTalents]);

  const handleFilterMenuOpenClose = useCallback(() => {
    setIsFilterMenuOpen((prev) => !prev);
  }, []);

  const handleQuickFilterChange = useCallback(
    (quickFilter: TalentQuickFilterButton | "", filters: SearchFilter[]) => {
      setSelectedQuickFilter(quickFilter);
      setFilters(filters);
      setFiltersToLocalStorage(filters);

      if (quickFilter) {
        setQuickFilterToLocalStorage(quickFilter);
      } else {
        removeQuickFilterFromLocalStorage();
      }
    },
    [],
  );

  const updateFilters = useCallback((filters: SearchFilter[]) => {
    setFilters(filters);
    setFiltersToLocalStorage(filters);
    setSelectedQuickFilter("");
    removeQuickFilterFromLocalStorage();
  }, []);

  const nonSelectableTalentIds = useMemo(
    () =>
      pageData
        .filter((t) => t.account.status !== TalentStatus.ACTIVE)
        .map((t) => t.id),
    [pageData],
  );

  const displayedAndSelectedTalentIds = useMemo(() => {
    const allIds = new Set(allTalents.map((talent) => talent.id));
    return selectedTalentIds.filter((id) => allIds.has(id));
  }, [selectedTalentIds, allTalents]);

  const talentRows = pageData.map((talent) => (
    <TalentTableRow
      key={talent.id}
      id={talent.id}
      talent={talent}
      selectableRows={hasCustomerOrgFilter}
    />
  ));

  if (error) {
    return <Error />;
  }

  const paginationProps: TablePaginationProps = {
    count,
    onPageChange,
    pageIndex,
    pageSize: parseInt(TALENTS_PAGE_SIZE, 10),
  };

  return (
    <Grid
      container
      rowGap={3}
      sx={{ position: "relative" }}
      data-testid="talents-page-root"
    >
      <Grid item xs={12} display="flex" alignItems="center">
        <Heading1>{t("common:talents")}</Heading1>
      </Grid>
      <Grid item xs={12}>
        <TalentQuickFilterButtons2
          selectedQuickFilter={selectedQuickFilter}
          onQuickFilterChange={handleQuickFilterChange}
        />
      </Grid>
      <Grid item xs={6}>
        <FilterSearchField
          width="900px"
          isOpen={isFilterMenuOpen}
          toggleMenu={handleFilterMenuOpenClose}
          label={t("searchByNameAndFilter")}
          value={nameSearch}
          onChange={setNameSearch}
          searchHistory={false}
          hideSearchHistory
          translation="talentProfile"
          filters={filters}
          updateFilters={updateFilters}
          isLoading={isLoading}
          filtersLocalStorageName={TALENT_FILTERS_LOCAL_STORAGE}
          debounceSearchMs={400}
          trailing={<TalentSearchInfo />}
        >
          <TalentFilters
            filters={filters}
            updateFilters={updateFilters}
            handleMenuToggle={handleFilterMenuOpenClose}
            selectedQuickFilter={selectedQuickFilter}
          />
        </FilterSearchField>
      </Grid>
      <Grid container rowGap={1}>
        {isLoading ? (
          <LoadingSpinner sx={{ position: "absolute" }} />
        ) : talentRows.length === 0 && !searchInitiated ? (
          <Grid item xs={12}>
            {t("talentProfile:talentsNotFound")}
          </Grid>
        ) : (
          <Box width="100%" display="flex" flexDirection="column">
            <StripedTable
              pagination={paginationProps}
              sortProps={{
                value: sortBy,
                onChange: handleSortChange,
                options: sortOptions,
              }}
              columnHeaders={[
                t("name"),
                t("talentProfile:phoneNumberLong"),
                t("talentProfile:emailAddress"),
                t("talentProfile:statusLabel"),
                t("createdAt"),
                t("talentProfile:myHr"),
                t("workHistory:city"),
                t("talentProfile:talentTag"),
              ]}
              itemRows={talentRows}
              selectableRows={hasCustomerOrgFilter}
              selectedIds={selectedTalentIds}
              updateSelectedIds={setSelectedTalentIds}
              selectAllMode="merge"
              nonSelectableIds={nonSelectableTalentIds}
            />
            {hasCustomerOrgFilter && (
              <SelectionActionBar>
                <Button
                  disabled={displayedAndSelectedTalentIds.length === 0}
                  variant="contained"
                  sx={{ height: "42px", borderRadius: "20px" }}
                  onClick={() => setSendUserMessageDialogOpen(true)}
                  startIcon={<EmailIcon />}
                >
                  {t("common:sendEmailToTalents")}
                </Button>
              </SelectionActionBar>
            )}
          </Box>
        )}
      </Grid>
      <SendTalentsUserMessageDialog
        open={sendUserMessageDialogOpen}
        onClose={() => setSendUserMessageDialogOpen(false)}
        customerOrgId={customerOrgId}
        selectedTalentIds={displayedAndSelectedTalentIds}
        allTalents={allTalents}
        onRemoveRecipient={(talentId) =>
          setSelectedTalentIds((prev) => prev.filter((id) => id !== talentId))
        }
      />
    </Grid>
  );
};
